import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { FaEdit, FaTrash } from "react-icons/fa";
import { hasPermission, ROLES, FEATURES } from "../lib/rolesConfig";
import MultiActionScrollButton from "../components/MultiActionScrollButton";
import { commitFirestoreWrite, offlineAwareMessage } from "../utils/offline";

// Placeholder for incident data - in a real app, this would come from a backend or state management
// const initialIncidents = [
//     { id: 1, dateTime: '2024-05-28T10:00', type: 'Fall', description: 'Slipped in the hallway near the bathroom.', actionTaken: 'Assisted to a chair, checked for injuries. No visible injuries.', reportedBy: 'Jane S.' },
//     { id: 2, dateTime: '2024-05-27T08:30', type: 'Missed Medication', description: 'Morning blood pressure medication (Lisinopril) was missed.', actionTaken: 'Administered as soon as remembered at 9:15 AM. Notified Dr. Green.', reportedBy: 'John D.' },
// ];

const getIncidentTypeColor = (type) => {
  switch (type) {
    case "Fall":
      return "bg-red-500";
    case "Missed Medication":
      return "bg-orange-500";
    case "Adverse Event":
      return "bg-purple-500";
    case "Behavioral":
      return "bg-blue-500";
    case "Equipment Malfunction":
      return "bg-gray-500";
    default:
      return "bg-green-500";
  }
};

const IncidentReportingPage = () => {
  const { currentUser, activeCareProfile, userRole } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [isLoadingIncidents, setIsLoadingIncidents] = useState(true);
  const [isSubmittingIncident, setIsSubmittingIncident] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [dateTime, setDateTime] = useState("");
  const [incidentType, setIncidentType] = useState("Fall");
  const [description, setDescription] = useState("");
  const [actionTaken, setActionTaken] = useState("");

  // Edit state
  const [isEditingIncident, setIsEditingIncident] = useState(false);
  const [currentIncidentId, setCurrentIncidentId] = useState(null);
  const cardRefs = useRef({});

  // Load more state
  const [visibleRecordsCount, setVisibleRecordsCount] = useState(5);
  const [sortOrder] = useState("desc");

  const userRoleForProfile = activeCareProfile
    ? (userRole && userRole[activeCareProfile.id]) ||
      (activeCareProfile.userId === currentUser?.uid ? ROLES.OWNER : undefined)
    : undefined;

  const canWrite = hasPermission(
    userRoleForProfile,
    FEATURES.INCIDENT_REPORTING,
    "write",
  );
  const canRead = hasPermission(
    userRoleForProfile,
    FEATURES.INCIDENT_REPORTING,
    "read",
  );

  useEffect(() => {
    if (!canRead || !activeCareProfile) {
      setIncidents([]);
      setIsLoadingIncidents(false);
      return;
    }
    setIsLoadingIncidents(true);
    const q = query(
      collection(db, "incidentReports"),
      where("careProfileId", "==", activeCareProfile.id),
      orderBy("dateTime", "desc"),
      limit(100),
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setIncidents(data);
        setIsLoadingIncidents(false);
      },
      (err) => {
        console.error("Error listening incidents:", err);
        setIsLoadingIncidents(false);
      },
    );
    return () => unsubscribe();
  }, [canRead, activeCareProfile]);

  const toJsDate = (dt) => {
    if (!dt) return null;
    if (dt.toDate) return dt.toDate(); // Firestore Timestamp
    return new Date(dt); // ISO string or milliseconds
  };

  const sortedIncidents = React.useMemo(() => {
    return [...incidents].sort((a, b) => {
      const dateA = toJsDate(a.dateTime);
      const dateB = toJsDate(b.dateTime);
      if (!dateA || !dateB) return 0;
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [incidents, sortOrder]);

  const handleLoadMore = () => {
    setVisibleRecordsCount((prevCount) => prevCount + 5);
  };

  const resetForm = () => {
    setDateTime("");
    setIncidentType("Fall");
    setDescription("");
    setActionTaken("");
    setShowForm(false);
    setIsEditingIncident(false);
    setCurrentIncidentId(null);
  };

  const handleCancelEdit = () => {
    const idToScrollTo = currentIncidentId;
    resetForm();
    if (idToScrollTo && cardRefs.current[idToScrollTo]) {
      setTimeout(() => {
        cardRefs.current[idToScrollTo].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  const handleEditIncident = (incident) => {
    if (!canWrite)
      return alert("You don't have permission to edit incident reports.");
    setIsEditingIncident(true);
    setCurrentIncidentId(incident.id);
    setDateTime(formatTimestampForInput(incident.dateTime));
    setIncidentType(incident.type);
    setDescription(incident.description);
    setActionTaken(incident.actionTaken);
    setShowForm(true);
    setTimeout(() => {
      const formElement = document.getElementById("incident-form");
      if (formElement) {
        formElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const handleDeleteIncident = async (id) => {
    if (!canWrite)
      return alert("You don't have permission to delete incident reports.");
    if (
      window.confirm("Are you sure you want to delete this incident report?")
    ) {
      try {
        setDeletingId(id);
        await commitFirestoreWrite(
          deleteDoc(doc(db, "incidentReports", id)),
          "Delete incident report",
        );
        window.alert(
          offlineAwareMessage("Incident report deleted successfully!"),
        );
        if (id === currentIncidentId) resetForm();
      } catch (error) {
        console.error("Error deleting incident: ", error);
        window.alert(`Failed to delete incident report. ${error.message}`);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleSubmitIncident = async (e) => {
    e.preventDefault();
    if (!canWrite)
      return alert("You don't have permission to save incident reports.");
    if (!currentUser || !activeCareProfile) {
      window.alert(
        "User not authenticated or no active care profile selected. Cannot save incident report.",
      );
      return;
    }
    if (!dateTime || !description || !actionTaken) {
      window.alert("Please fill in Date/Time, Description, and Action Taken.");
      return;
    }

    const incidentData = {
      dateTime: Timestamp.fromDate(new Date(dateTime)),
      type: incidentType,
      description,
      actionTaken,
    };

    try {
      setIsSubmittingIncident(true);
      if (isEditingIncident && currentIncidentId) {
        await commitFirestoreWrite(
          updateDoc(doc(db, "incidentReports", currentIncidentId), {
            ...incidentData,
            lastModifiedAt: Timestamp.now(),
            lastModifiedBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || currentUser.email,
            },
          }),
          "Update incident report",
        );
      } else {
        const incidentRef = doc(collection(db, "incidentReports"));
        await commitFirestoreWrite(
          setDoc(incidentRef, {
            ...incidentData,
            careProfileId: activeCareProfile.id,
            userId: activeCareProfile.userId,
            createdAt: Timestamp.now(),
            createdBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || currentUser.email,
            },
            lastModifiedAt: Timestamp.now(),
            lastModifiedBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || currentUser.email,
            },
          }),
          "Create incident report",
        );
      }
      window.alert(
        offlineAwareMessage(
          `Incident report ${isEditingIncident ? "updated" : "created"} successfully!`,
        ),
      );
      resetForm();
    } catch (error) {
      console.error(
        `Error ${isEditingIncident ? "updating" : "creating"} incident report:`,
        error,
      );
      window.alert(
        `Failed to ${isEditingIncident ? "update" : "create"} incident report. ${error.message}`,
      );
    } finally {
      setIsSubmittingIncident(false);
    }
  };

  // Helper to format Firestore Timestamp for display
  const formatIncidentTimestamp = (timestamp) => {
    const date = toJsDate(timestamp);
    return date ? date.toLocaleString() : "N/A";
  };

  // Helper to format Firestore Timestamp for datetime-local input
  // This will be needed if we add an edit feature
  const formatTimestampForInput = (timestamp) => {
    const date = toJsDate(timestamp);
    if (!date) return "";

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getSubmitLabel = () => {
    if (isSubmittingIncident) return "Saving...";
    return isEditingIncident ? "Update Report" : "Submit Report";
  };

  return (
    <div className="container mx-auto p-4 md:p-6 lg:p-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
        <h1 className="text-4xl font-bold text-red-700 mb-4 md:mb-0">
          Incident Reporting
        </h1>
        {canWrite && (
          <button
            onClick={() => {
              resetForm();
              setDateTime(formatTimestampForInput(new Date()));
              setShowForm(true);
            }}
            className="px-4 py-2 bg-orange-600 text-white font-semibold rounded-lg shadow-md hover:bg-orange-700 transition duration-150 ease-in-out"
          >
            Report New Incident
          </button>
        )}
      </div>

      {showForm && (
        <div
          id="incident-form"
          className="mb-8 bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500"
        >
          <h3 className="text-2xl font-semibold text-gray-700 mb-4">
            {isEditingIncident
              ? "Edit Incident Report"
              : "Report a New Incident"}
          </h3>
          <form onSubmit={handleSubmitIncident} className="space-y-4">
            {/* Date & Time */}
            <div>
              <label
                htmlFor="dateTime"
                className="block text-sm font-medium text-gray-700"
              >
                Date & Time of Incident*
              </label>
              <input
                id="dateTime"
                type="datetime-local"
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>

            {/* Incident Type */}
            <div>
              <label
                htmlFor="incidentType"
                className="block text-sm font-medium text-gray-700"
              >
                Type of Incident
              </label>
              <select
                id="incidentType"
                value={incidentType}
                onChange={(e) => setIncidentType(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option>Fall</option>
                <option>Missed Medication</option>
                <option>Adverse Event</option>
                <option>Behavioral</option>
                <option>Equipment Malfunction</option>
                <option>Other</option>
              </select>
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                Description of Incident*
              </label>
              <textarea
                id="description"
                rows="4"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              ></textarea>
            </div>

            {/* Action Taken */}
            <div className="md:col-span-2">
              <label
                htmlFor="actionTaken"
                className="block text-sm font-medium text-gray-700"
              >
                Action Taken*
              </label>
              <textarea
                id="actionTaken"
                rows="3"
                value={actionTaken}
                onChange={(e) => setActionTaken(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              ></textarea>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-end items-stretch">
              <button
                type="submit"
                disabled={isSubmittingIncident}
                className={`px-8 py-3 text-center font-semibold rounded-lg shadow-md transition duration-150 ease-in-out w-full sm:w-auto order-first sm:order-last ${isSubmittingIncident ? "bg-gray-400 cursor-not-allowed" : "bg-orange-600 text-white hover:bg-orange-700"}`}
              >
                {getSubmitLabel()}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-8 py-3 text-center text-gray-700 bg-gray-200 font-semibold rounded-lg shadow-md hover:bg-gray-300 transition duration-150 ease-in-out w-full sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mt-4">
        {isLoadingIncidents ? (
          <p>Loading incidents...</p>
        ) : sortedIncidents.length > 0 ? (
          <div className="space-y-4">
            {sortedIncidents.slice(0, visibleRecordsCount).map((incident) => (
              <div
                key={incident.id}
                ref={(el) => (cardRefs.current[incident.id] = el)}
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-400 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span
                      className={`inline-block px-3 py-1 text-sm font-semibold text-white rounded-full mb-2 ${getIncidentTypeColor(incident.type)}`}
                    >
                      {incident.type}
                    </span>
                    <p className="text-sm text-gray-500">
                      {formatIncidentTimestamp(incident.dateTime)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    {canWrite && (
                      <>
                        <button
                          onClick={() => handleEditIncident(incident)}
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                        >
                          <FaEdit size="1.2em" />
                        </button>
                        <button
                          onClick={() => handleDeleteIncident(incident.id)}
                          className={`text-red-500 hover:text-red-700 transition-colors ${deletingId === incident.id ? "opacity-50 cursor-not-allowed" : ""}`}
                          disabled={deletingId === incident.id}
                        >
                          <FaTrash size="1.2em" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <p className="font-bold text-gray-700">Description</p>
                  <p className="text-gray-600 whitespace-pre-wrap">
                    {incident.description}
                  </p>
                </div>
                <div className="mt-3">
                  <p className="font-bold text-gray-700">Action Taken</p>
                  <p className="text-gray-600 whitespace-pre-wrap">
                    {incident.actionTaken}
                  </p>
                </div>
                <div className="mt-3 border-t pt-3 text-sm text-gray-500">
                  {incident.createdBy && (
                    <p>
                      Reported by:{" "}
                      <span className="font-semibold">
                        {incident.createdBy.name || "N/A"}
                      </span>
                    </p>
                  )}
                  {incident.lastModifiedBy &&
                    incident.lastModifiedBy.uid !==
                      activeCareProfile.userId && (
                      <p>
                        Last updated by:{" "}
                        <span className="font-semibold">
                          {incident.lastModifiedBy.name}
                        </span>
                      </p>
                    )}
                </div>
              </div>
            ))}
            {visibleRecordsCount < sortedIncidents.length && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-2 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300 ease-in-out"
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-500 text-lg">No incidents recorded yet.</p>
            <p className="text-sm text-gray-400 mt-2">
              Click "Report New Incident" to add the first one.
            </p>
          </div>
        )}
      </div>
      <MultiActionScrollButton scrollContainerId="main-content" />
    </div>
  );
};

export default IncidentReportingPage;
